import React, { useEffect, useMemo, useState } from 'react';
import { ArchiveBoxIcon } from '@heroicons/react/24/outline';
import {
  forkTaskTemplateAdmin,
  getTaskTemplateAdminDetail,
  normalizeTemplatePhases,
  synthesizeTemplateFromCourtData,
  synthesizeSubtypeTemplates,
  type TaskTemplateDetailResponse,
  type TaskTemplateListItem,
} from './templateManagementService';
import {
  getSourceDefinition,
  type SourceDefinitionInfo,
  STATUS_LABELS as WF_STATUS_LABELS,
  STATUS_COLORS as WF_STATUS_COLORS,
  type WorkflowStatus,
} from '../workflow-definitions/workflowDefinitionService';
import TemplateDetailTimeline from './TemplateDetailTimeline';
import TemplateDetailForms from './TemplateDetailForms';
import TemplateDetailCourtInfo from './TemplateDetailCourtInfo';
import TemplateDetailStatutes from './TemplateDetailStatutes';
import TemplateDetailHistory from './TemplateDetailHistory';

type DetailTab = 'timeline' | 'forms' | 'court-info' | 'statutes' | 'history';

interface TemplateDetailProps {
  templateId: string;
  onBack: () => void;
  onEditTemplate?: (templateId: string) => void;
  onTemplateForked?: (templateId: string) => void;
  onArchiveTemplate?: (template: import('./templateManagementService').TaskTemplateListItem) => void;
}

const TABS: Array<{ id: DetailTab; label: string }> = [
  { id: 'timeline', label: 'Timeline' },
  { id: 'forms', label: 'Forms & Documents' },
  { id: 'court-info', label: 'Court Info' },
  { id: 'statutes', label: 'Statutes' },
  { id: 'history', label: 'History' },
];

function formatTemplateKind(kind?: string): string {
  if (!kind) return '';
  return kind.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function confidenceDot(confidence?: string): string {
  if (confidence === 'high') return 'bg-emerald-500';
  if (confidence === 'medium') return 'bg-amber-500';
  if (confidence === 'low') return 'bg-rose-500';
  return 'bg-gray-400';
}

const TemplateDetail: React.FC<TemplateDetailProps> = ({ templateId, onBack, onEditTemplate, onTemplateForked, onArchiveTemplate }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<TaskTemplateDetailResponse | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>('timeline');
  const [forking, setForking] = useState<boolean>(false);
  const [regenerating, setRegenerating] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sourceDef, setSourceDef] = useState<SourceDefinitionInfo | null>(null);
  const [sourceDefLoading, setSourceDefLoading] = useState(false);

  const loadDetail = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      const response = await getTaskTemplateAdminDetail(id);
      setDetail(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    loadDetail(templateId).then(() => { if (!mounted) setDetail(null); });
    return () => { mounted = false; };
  }, [templateId]);

  const resolvedTemplate = detail?.resolved_template || detail?.template;
  const phases = useMemo(() => normalizeTemplatePhases(resolvedTemplate), [resolvedTemplate]);

  // Load source workflow definition for this template
  useEffect(() => {
    if (!resolvedTemplate?.jurisdiction_code || !resolvedTemplate?.practice_area) {
      setSourceDef(null);
      return;
    }
    let mounted = true;
    const stateCode = (resolvedTemplate.jurisdiction_code.split('-')[0] || '').toUpperCase();
    if (!stateCode) return;

    setSourceDefLoading(true);
    getSourceDefinition(stateCode, resolvedTemplate.practice_area, resolvedTemplate.case_subtype || undefined)
      .then((def) => { if (mounted) setSourceDef(def); })
      .catch(() => { if (mounted) setSourceDef(null); })
      .finally(() => { if (mounted) setSourceDefLoading(false); });

    return () => { mounted = false; };
  }, [resolvedTemplate?.jurisdiction_code, resolvedTemplate?.practice_area, resolvedTemplate?.case_subtype]);

  const handleCreateVariant = async () => {
    if (!detail?.template) return;
    try {
      setForking(true);
      setError(null);
      const fork = await forkTaskTemplateAdmin(detail.template.id);
      setSuccessMessage(`Firm variant created: ${fork.name}`);
      onTemplateForked?.(fork.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create firm variant');
    } finally {
      setForking(false);
    }
  };

  const handleEditTemplate = () => {
    if (!detail?.template) return;
    onEditTemplate?.(detail.template.id);
  };

  const handleRegenerate = async () => {
    if (!resolvedTemplate?.jurisdiction_code || !resolvedTemplate?.practice_area) return;
    try {
      setRegenerating(true);
      setError(null);
      setSuccessMessage(null);

      if (resolvedTemplate.case_subtype) {
        // Regenerate all subtypes for this jurisdiction + practice area
        const result = await synthesizeSubtypeTemplates({
          jurisdiction_code: resolvedTemplate.jurisdiction_code,
          practice_area: resolvedTemplate.practice_area,
        });
        setSuccessMessage(
          `Regenerated master + ${result.subtypes.length} subtype templates for ${resolvedTemplate.practice_area} [${resolvedTemplate.jurisdiction_code}].`,
        );
      } else {
        // Regenerate just this master template
        const result = await synthesizeTemplateFromCourtData({
          jurisdiction_code: resolvedTemplate.jurisdiction_code,
          practice_area: resolvedTemplate.practice_area,
        });
        const action = result.was_updated ? 'Updated' : 'Created';
        setSuccessMessage(
          `${action} template "${result.template.name}" — ${result.evidence.forms_total} forms, ${result.evidence.judges_total} judges, ${result.evidence.fees_total} fees.`,
        );
      }

      // Reload detail to reflect changes
      await loadDetail(templateId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate template');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <div className="text-sm text-gray-600 dark:text-gray-300">Loading template details...</div>
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div className="space-y-3">
        <button
          onClick={onBack}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          Back to Templates
        </button>
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
          {error}
        </div>
      </div>
    );
  }

  if (!detail || !resolvedTemplate) {
    return <div className="text-sm text-gray-600 dark:text-gray-300">Template details are unavailable.</div>;
  }

  const templateKind = resolvedTemplate.template_kind || 'base';
  const caseSubtype = resolvedTemplate.case_subtype;
  const canRegenerate = resolvedTemplate.is_system_template && resolvedTemplate.jurisdiction_code && resolvedTemplate.practice_area;

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
      >
        Back to Templates
      </button>

      {/* Enhanced Header Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-wrap items-start justify-between gap-3">
          {/* Title + Badges */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{resolvedTemplate.name}</h3>
              {/* Subtype badge */}
              {caseSubtype && (
                <span className="inline-flex items-center rounded-full bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-800 dark:bg-violet-900/30 dark:text-violet-200">
                  {caseSubtype.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </span>
              )}
              {/* Template kind badge */}
              {templateKind !== 'base' && (
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                  templateKind === 'jurisdiction_variant'
                    ? 'bg-lex-accent-soft text-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent'
                    : templateKind === 'firm_variant'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                      : 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-200'
                }`}>
                  {formatTemplateKind(templateKind)}
                </span>
              )}
              {/* System / Firm badge */}
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                resolvedTemplate.is_system_template
                  ? 'bg-lex-accent-soft text-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
              }`}>
                {resolvedTemplate.is_system_template ? 'System' : 'Firm Custom'}
              </span>
              {/* Lifecycle badge */}
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                resolvedTemplate.lifecycle_status === 'validated'
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                  : resolvedTemplate.lifecycle_status === 'stale'
                    ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200'
              }`}>
                {resolvedTemplate.lifecycle_status || 'draft'}
              </span>
            </div>
            <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-300">
              {resolvedTemplate.description || 'No description provided.'}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 shrink-0">
            {canRegenerate && (
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
              >
                {regenerating ? 'Regenerating...' : 'Regenerate'}
              </button>
            )}
            {detail.template.is_system_template ? (
              <>
                <button
                  onClick={handleCreateVariant}
                  disabled={forking}
                  className="rounded-md bg-lex-accent px-3 py-1.5 text-sm font-medium text-lex-accent-contrast hover:bg-lex-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {forking ? 'Creating Variant...' : 'Create Firm Variant'}
                </button>
                {detail.template.template_kind === 'jurisdiction_base' && onEditTemplate && (
                  <button
                    onClick={handleEditTemplate}
                    className="rounded-md border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                  >
                    Edit Base
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={handleEditTemplate}
                className="rounded-md bg-lex-accent px-3 py-1.5 text-sm font-medium text-lex-accent-contrast hover:bg-lex-accent-hover"
              >
                Edit
              </button>
            )}
            {(!detail.template.is_system_template || detail.template.template_kind === 'jurisdiction_base') && onArchiveTemplate && (
              <button
                onClick={() => {
                  const t = detail.template as TaskTemplateListItem;
                  onArchiveTemplate({
                    ...t,
                    usage_count: detail.usage_count,
                    task_count: detail.task_count,
                    phase_count: detail.phase_count,
                    form_count: detail.form_count,
                    catalog_form_count: detail.catalog_form_count,
                  });
                }}
                className="flex items-center gap-1 rounded-md border border-amber-300 dark:border-amber-700 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors duration-150"
              >
                <ArchiveBoxIcon className="h-4 w-4" />
                Archive
              </button>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          <div className="rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-700/50">
            <span className="block text-xs text-gray-500 dark:text-gray-400">Practice Area</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{resolvedTemplate.practice_area || 'Unspecified'}</span>
          </div>
          <div className="rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-700/50">
            <span className="block text-xs text-gray-500 dark:text-gray-400">Jurisdiction</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{resolvedTemplate.jurisdiction_code || 'Generic'}</span>
            {resolvedTemplate.jurisdiction?.court_name && (
              <span className="block text-[11px] text-gray-400 dark:text-gray-500 truncate">{resolvedTemplate.jurisdiction.court_name}</span>
            )}
          </div>
          <div className="rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-700/50">
            <span className="block text-xs text-gray-500 dark:text-gray-400">Tasks</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{detail.task_count}</span>
          </div>
          <div className="rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-700/50">
            <span className="block text-xs text-gray-500 dark:text-gray-400">Phases</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{detail.phase_count}</span>
          </div>
          <div className="rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-700/50">
            <span className="block text-xs text-gray-500 dark:text-gray-400">Workflow Forms</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{detail.form_count}</span>
          </div>
          {detail.catalog_form_count > 0 && detail.catalog_form_count !== detail.form_count && (
            <div className="rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-700/50">
              <span className="block text-xs text-gray-500 dark:text-gray-400">Catalog Forms</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{detail.catalog_form_count}</span>
            </div>
          )}
          <div className="rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-700/50">
            <span className="block text-xs text-gray-500 dark:text-gray-400">Confidence</span>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
              <span className={`h-2 w-2 rounded-full ${confidenceDot(resolvedTemplate.confidence_score)}`} />
              {resolvedTemplate.confidence_score || 'N/A'}
            </span>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
            {successMessage}
          </div>
        )}
      </div>

      {/* Source Statutory Definition */}
      {(sourceDef || sourceDefLoading) && (
        <div className="rounded-lg border border-lex-accent dark:border-lex-accent bg-gradient-to-r from-lex-accent-soft to-purple-50 dark:from-lex-accent-soft dark:to-purple-900/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-lex-accent dark:text-lex-accent flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
            <span className="text-xs font-semibold uppercase tracking-wider text-lex-accent dark:text-lex-accent">
              Source Statutory Definition
            </span>
          </div>

          {sourceDefLoading ? (
            <div className="flex items-center gap-2 py-1">
              <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-lex-accent border-t-transparent" />
              <span className="text-xs text-lex-accent dark:text-lex-accent">Looking up source definition...</span>
            </div>
          ) : sourceDef ? (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {sourceDef.label}
                  </p>
                  {(() => {
                    const colors = WF_STATUS_COLORS[sourceDef.status as WorkflowStatus] || WF_STATUS_COLORS.draft;
                    return (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${colors.bg} ${colors.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
                        {WF_STATUS_LABELS[sourceDef.status as WorkflowStatus] || sourceDef.status}
                      </span>
                    );
                  })()}
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    v{sourceDef.version}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>{sourceDef.phase_count} phase{sourceDef.phase_count !== 1 ? 's' : ''}, {sourceDef.task_count} task{sourceDef.task_count !== 1 ? 's' : ''}</span>
                  {sourceDef.source_authority && (
                    <span className="truncate" title={sourceDef.source_authority}>
                      {sourceDef.source_authority}
                    </span>
                  )}
                  {sourceDef.published_at && (
                    <span>Published {new Date(sourceDef.published_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-lex-accent-soft dark:bg-lex-accent-soft text-lex-accent dark:text-lex-accent" title="This template was synthesized from this statutory definition. Edit it in the Statutory Library tab.">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.875-3.375l4.5-4.5a4.5 4.5 0 00-6.364-6.364L8.257 2.879" />
                  </svg>
                  Statutory Library
                </span>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Tabs */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex flex-wrap gap-2">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-t-md border-b-2 px-3 py-2 text-sm font-medium ${
                  activeTab === tab.id
                    ? 'border-lex-accent text-lex-accent dark:text-lex-accent'
                    : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'timeline' && <TemplateDetailTimeline phases={phases} />}
        {activeTab === 'forms' && <TemplateDetailForms phases={phases} />}
        {activeTab === 'court-info' && <TemplateDetailCourtInfo template={resolvedTemplate} phases={phases} />}
        {activeTab === 'statutes' && <TemplateDetailStatutes template={resolvedTemplate} phases={phases} />}
        {activeTab === 'history' && (
          <TemplateDetailHistory
            template={resolvedTemplate}
            usageCount={detail.usage_count}
            inheritanceChain={detail.inheritance_chain}
            onRolledBack={() => loadDetail(templateId)}
          />
        )}
      </div>
    </div>
  );
};

export default TemplateDetail;
